import React from 'react';
import { faFont, faBold, faItalic } from '@fortawesome/pro-regular-svg-icons';
import { faBold as faBoldSelect, faItalic as faItalicSelect } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ToolBarColorPicker from '../../components/ToolbarColorPicker';
import FontFamilySelect from '../../components/FontFamilySelect';
import SelectFontSize from '../../components/SelectFontSize';

/**
 * @param {Object} props
 * @param {import('actions/scene.types').CalendarShape} props.shape Calendar shape being edited.
 * @param {import('actions/scene.types').CalendarShapeChangeHandler} props.onChange Applies a partial calendar update.
 * @param {Object} [props.toolbarRef] Toolbar element ref used to position popovers.
 * @param {string} [props.className] Optional class for the labelled admin layout.
 * @returns {React.ReactElement}
 */
export const EventFields = ({ shape, onChange, toolbarRef, className }) => {
  /** @type {Partial<import('actions/scene.types').CalendarEventStyle>} */
  const eventStyle = shape.eventStyle || {};
  const fontSizeValue = Number(eventStyle.fontSize ?? (shape.dayFontSize ? shape.dayFontSize - 2 : 6)) || 6;
  const fontSize = (
    <SelectFontSize value={fontSizeValue} onChange={(value) => onChange({ eventStyle: { fontSize: value } })} />
  );
  const fontFamily = (
    <FontFamilySelect
      initialValue={eventStyle.fontFamily || shape.fontFamily}
      onChange={(value) => onChange({ eventStyle: { fontFamily: value } })}
    />
  );
  const style = (
    <>
      <div className="icon-button-container" onClick={() => onChange({ eventStyle: { isBold: !eventStyle.isBold } })}>
        <FontAwesomeIcon.Memo icon={eventStyle.isBold ? faBoldSelect : faBold} className="icon-button" />
      </div>
      <div
        className="icon-button-container"
        onClick={() => onChange({ eventStyle: { isItalic: !eventStyle.isItalic } })}
      >
        <FontAwesomeIcon.Memo icon={eventStyle.isItalic ? faItalicSelect : faItalic} className="icon-button" />
      </div>
    </>
  );
  const colour = (
    <ToolBarColorPicker
      initialValue={eventStyle.fill || shape.mainColor}
      toolbarRef={toolbarRef}
      onChange={(value) => onChange({ eventStyle: { fill: value } })}
      icon={faFont}
      alignPickerLeft={!!className}
    />
  );

  if (className) {
    /**
     * @param {string} label
     * @param {React.ReactNode} control
     * @param {string} [controlClassName]
     */
    const labelledField = (label, control, controlClassName = 'typography-field-control') => (
      <div className="typography-field">
        <div className="typography-field-label">{label}</div>
        <div className={controlClassName}>{control}</div>
      </div>
    );

    return (
      <div className={className}>
        {labelledField('Font size', fontSize)}
        {labelledField('Font family', fontFamily)}
        {labelledField('Font style', style, 'typography-field-control typography-style-controls')}
        {labelledField('Text colour', colour, 'typography-field-control typography-colour-control')}
      </div>
    );
  }

  return (
    <>
      {fontSize}
      {fontFamily}
      {style}
      {colour}
    </>
  );
};

export default EventFields;
